import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { ProfileSection } from '../components/settings/ProfileSection';
import { CompanyNameCard, LogoCard, ThemeColorsCard, DisplayModeCard, PreviewCard, DriveCard } from '../components/settings/SettingsCards';
import { GoogleAccountCard } from '../components/settings/GoogleAccountCard';
import { WorkingHoursCard } from '../components/settings/WorkingHoursCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { setLanguage: setGlobalLanguage } = useLanguage();
  const { user, setUser } = useAuth();
  const [settings, setSettings] = useState({ company_name: 'BATI-BOYS', logo_url: '', primary_color: '#C62828', theme: 'default' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const fileInputRef = useRef(null);
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });
  const [driveStatus, setDriveStatus] = useState({ connected: false, email: '' });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
      setSettings(response.data);
      setLogoPreview(response.data.logo_url ? `${API_URL}${response.data.logo_url}` : '');
    } catch (error) {
      console.error('Erreur chargement parametres:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) setProfileForm({ name: user.name || '', email: user.email || '' }); }, [user]);

  useEffect(() => {
    fetchSettings();
    axios.get(`${API_URL}/api/drive/status`, { withCredentials: true }).then(res => setDriveStatus(res.data)).catch((e) => { console.error('Erreur status Drive:', e); });
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') { toast.success('Google Drive connecte avec succes !'); setDriveStatus({ connected: true, email: '' }); window.history.replaceState({}, '', window.location.pathname); }
    if (params.get('drive_error')) { toast.error(`Erreur Drive: ${params.get('drive_error')}`); window.history.replaceState({}, '', window.location.pathname); }
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, { withCredentials: true });
      setGlobalLanguage(settings.language || 'fr');
      toast.success('Parametres enregistres avec succes');
      window.location.reload();
    } catch (error) { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const handleColorSelect = (color) => setSettings(prev => ({ ...prev, primary_color: color }));

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">Personnalisation</h1>
        <p className="page-subtitle">Personnalisez l'apparence de votre application</p>
      </div>

      <div style={{ display: 'grid', gap: '24px', maxWidth: '800px' }}>
        <ProfileSection user={user} setUser={setUser} profileForm={profileForm} setProfileForm={setProfileForm} />
        <GoogleAccountCard isAdmin={user?.role === 'admin'} />
        <WorkingHoursCard isAdmin={user?.role === 'admin'} />
        <CompanyNameCard settings={settings} setSettings={setSettings} />
        <LogoCard logoPreview={logoPreview} setLogoPreview={setLogoPreview} settings={settings} setSettings={setSettings} uploading={uploading} setUploading={setUploading} fileInputRef={fileInputRef} />
        <ThemeColorsCard settings={settings} onColorSelect={handleColorSelect} />
        <DisplayModeCard settings={settings} setSettings={setSettings} />

        {user?.role === 'admin' && (
          <div className="card" data-testid="labor-coefficient-card">
            <div className="card-header"><h3 className="card-title">Coefficient main d'œuvre</h3></div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ fontSize: '0.82rem', color: '#5E5F66', margin: '0 0 10px' }}>
                Multiplicateur appliqué au salaire brut pour estimer le coût chargé sur chaque chantier
                (cotisations patronales, mutuelle, prévoyance…). Valeur courante : <strong>1.42</strong>.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="number" step="0.01" min="1" max="3"
                  className="form-input"
                  data-testid="labor-coefficient-input"
                  value={settings.labor_cost_coefficient ?? 1.42}
                  onChange={(e) => setSettings((s) => ({ ...s, labor_cost_coefficient: parseFloat(e.target.value) || 1.42 }))}
                  style={{ width: 140 }}
                />
                <span style={{ fontSize: '0.85rem', color: '#5E5F66' }}>× salaire journalier brut</span>
              </div>
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #E2E2E6' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '0.88rem' }}>Seuil d'alerte "marge sous tension"</h4>
                <p style={{ fontSize: '0.78rem', color: '#5E5F66', margin: '0 0 8px' }}>
                  Quand le coût réel (dépenses + main d'œuvre + frais salariés) dépasse ce % du prix de vente HT,
                  un badge rouge apparaît sur le chantier et tu reçois une notification.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="number" step="1" min="50" max="100"
                    className="form-input"
                    data-testid="margin-alert-threshold-input"
                    value={settings.margin_alert_threshold ?? 80}
                    onChange={(e) => setSettings((s) => ({ ...s, margin_alert_threshold: parseFloat(e.target.value) || 80 }))}
                    style={{ width: 100 }}
                  />
                  <span style={{ fontSize: '0.85rem', color: '#5E5F66' }}>% du prix de vente HT</span>
                </div>
              </div>
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #E2E2E6' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: '0.88rem' }}>Email du comptable (variables de paie)</h4>
                <p style={{ fontSize: '0.78rem', color: '#5E5F66', margin: '0 0 8px' }}>
                  Adresse à laquelle le bouton "Envoyer au comptable" (Finance &gt; Variables de paie) envoie le fichier Excel mensuel.
                </p>
                <input
                  type="email"
                  className="form-input"
                  data-testid="comptable-email-input"
                  value={settings.comptable_email || ''}
                  onChange={(e) => setSettings((s) => ({ ...s, comptable_email: e.target.value }))}
                  placeholder="comptable@cabinet.fr"
                  style={{ width: '100%', maxWidth: 360 }}
                />
              </div>
            </div>
          </div>
        )}

        <PreviewCard settings={settings} logoPreview={logoPreview} />
        <DriveCard driveStatus={driveStatus} setDriveStatus={setDriveStatus} />

        <button data-testid="save-settings-btn" className="btn btn-primary" onClick={handleSave} disabled={saving}
          style={{ width: '100%', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <FloppyDisk size={20} weight="bold" /> {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
